/**
 * Single source of truth for where a notification navigates when clicked.
 *
 * Used by both the notification bell dropdown (ContentHeader) and the full
 * notifications page so they can never drift apart. Returns either:
 *   - { path, state? } describing the destination, or
 *   - null when the notification has no navigation target (e.g. security
 *     notices like password_changed) or needs special handling by the caller
 *     (team_invitation opens an accept/decline modal — handled separately).
 *
 * @param {{ notification_type: string, metadata?: object }} notification
 * @returns {{ path: string, state?: object } | null}
 */
export function getNotificationRoute(notification) {
  const { notification_type: type, metadata = {} } = notification || {};

  // Affirmation family → the question, opened at its summary view.
  if (['affirmation', 'pending_affirmation', 'affirmation_reset'].includes(type)) {
    if (metadata.question_id) {
      return { path: `/questionnaire/${metadata.question_id}`, state: { startAtSummary: true } };
    }
  }

  // Advocacy workbook notifications → the specific exercise in the team workbook.
  // Both the "advocacy_remind" nudge and the collaborative-completion event carry
  // exercise_id + team_id. These must NOT fall through to the generic /progress
  // (nudge) or /team/:id (team_id) branches below.
  const isAdvocacyNudge = type === 'nudge' && metadata.kind === 'advocacy_remind';
  if (isAdvocacyNudge || type === 'collab_exercise_completed') {
    if (metadata.team_id && metadata.exercise_id) {
      return { path: `/workbook/team/${metadata.team_id}/exercise/${metadata.exercise_id}` };
    }
    return { path: '/workbook' };
  }

  // Any Care Fund action that needs attention (member confirmation, amendment
  // to confirm, withdrawal to approve, contribution receipt to confirm) →
  // the team's Care Fund page, where the pending/amendment cards live.
  if (typeof metadata.kind === 'string' && metadata.kind.startsWith('care_fund_') && metadata.team_id) {
    return { path: `/team/${metadata.team_id}/care-fund` };
  }

  // Rites & Remembrance task assigned / due today → that plan's checklist.
  if (metadata.route === 'rites' && metadata.team_id) {
    if (metadata.plan_id) return { path: `/team/${metadata.team_id}/remembrance/${metadata.plan_id}` };
    return { path: `/team/${metadata.team_id}?tab=rites` };
  }

  // Questionnaire nudge → Your Progress.
  if (type === 'nudge') {
    return { path: '/progress' };
  }

  // Plan/subscription settled (FR-2.4 dual confirmation) → the team's
  // billing page, where the unlock and its sponsor acknowledgment live.
  if (type === 'plan_unlocked' && metadata.team_id) {
    return { path: `/team/${metadata.team_id}/billing` };
  }

  // Post notifications → the team's Posts tab, deep-linked to the post so it
  // can be scrolled to and highlighted.
  if (['post_mention', 'post_comment', 'post_reaction'].includes(type)) {
    if (metadata.team_id) {
      const postParam = metadata.post_id ? `&post=${metadata.post_id}` : '';
      return { path: `/team/${metadata.team_id}?tab=posts${postParam}` };
    }
  }

  // Team-scoped notifications → channel (deep-linked to a message if present)
  // or the team home.
  if (metadata.team_id) {
    if (metadata.channel_id) {
      const msgParam = metadata.message_id ? `?msg=${metadata.message_id}` : '';
      return { path: `/team/${metadata.team_id}/channels/${metadata.channel_id}${msgParam}` };
    }
    return { path: `/team/${metadata.team_id}` };
  }

  // No navigation target (security notices, etc.).
  return null;
}
